import { Suspense, useEffect, useRef, type FC } from "react";
import { Link, useParams } from "@tanstack/react-router";
import ArticleCard, {
  formatArticleDate,
} from "./articles/ArticleCard";
import TableOfContents from "./articles/TableOfContents";
import ReadingProgress from "./articles/ReadingProgress";
import ConsultCTA from "./sections/ConsultCTA";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import {
  getAdjacentArticles,
  getArticleBody,
  getArticleBySlug,
  getRelatedArticles,
} from "../lib/articles";
import {
  AUTHOR_NAME,
  articleImageUrl,
  articleUrl,
  buildArticleJsonLd,
} from "../lib/articleSeo";

const BodySkeleton: FC = () => (
  <div className="animate-pulse space-y-4" role="status" aria-label="Loading article">
    <div className="h-6 w-3/4 rounded bg-slate-800" />
    <div className="h-4 w-full rounded bg-slate-800" />
    <div className="h-4 w-11/12 rounded bg-slate-800" />
    <div className="h-4 w-5/6 rounded bg-slate-800" />
  </div>
);

const ArticlePage: FC = () => {
  // strict: false because the route id depends on the parent layout ("/_classic/articles/$slug")
  const { slug } = useParams({ strict: false }) as { slug: string };
  const article = getArticleBySlug(slug);
  const Body = getArticleBody(slug);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  useDocumentMeta(
    article
      ? {
          title: `${article.title} | Don Benary Lagadan`,
          description: article.summary,
          url: articleUrl(article.slug),
          image: articleImageUrl(article),
          type: "article",
          publishedTime: article.publishedAt,
          modifiedTime: article.updatedAt,
        }
      : null
  );

  // Scroll to top and move focus to the h1 when the article changes.
  useEffect(() => {
    window.scrollTo(0, 0);
    headingRef.current?.focus({ preventScroll: true });
  }, [slug]);

  // BlogPosting JSON-LD, removed on unmount.
  useEffect(() => {
    if (!article) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(buildArticleJsonLd(article));
    document.head.appendChild(script);
    return () => script.remove();
  }, [article]);

  if (!article || !Body) {
    return (
      <section className="mt-10 min-h-[50vh] py-14 text-center">
        <h1 className="mb-4 text-3xl font-bold text-white">Article not found</h1>
        <p className="mb-6 text-gray-400">
          That article doesn&apos;t exist or has moved.
        </p>
        <Link
          to="/articles"
          className="text-cyan-400 underline underline-offset-4 hover:text-cyan-300"
        >
          Back to all articles
        </Link>
      </section>
    );
  }

  const related = getRelatedArticles(article);
  const { previous, next } = getAdjacentArticles(article);
  const wasUpdated = article.updatedAt !== article.publishedAt;

  return (
    <>
      <ReadingProgress />
      <article className="mt-10 py-10">
        <header className="mb-10 max-w-3xl">
          <span className="mb-4 inline-block rounded-full bg-cyan-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-cyan-300">
            {article.category}
          </span>
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="mb-4 text-3xl font-bold text-white outline-none md:text-5xl"
          >
            {article.title}
          </h1>
          <p className="mb-4 text-lg text-gray-300">{article.summary}</p>
          <p className="flex flex-wrap items-center gap-x-3 text-sm text-gray-400">
            <span>
              Published{" "}
              <time dateTime={article.publishedAt}>
                {formatArticleDate(article.publishedAt)}
              </time>
            </span>
            {wasUpdated && (
              <span>
                Updated{" "}
                <time dateTime={article.updatedAt}>
                  {formatArticleDate(article.updatedAt)}
                </time>
              </span>
            )}
            <span>{article.readingMinutes} min read</span>
          </p>
        </header>

        {article.cover && (
          <img
            src={article.cover.src}
            alt={article.cover.alt}
            width={article.cover.width}
            height={article.cover.height}
            decoding="async"
            className="mb-10 h-auto w-full max-w-5xl rounded-2xl border border-white/5"
          />
        )}

        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_16rem] lg:gap-10">
          <div className="lg:order-2">
            <TableOfContents bodyRef={bodyRef} slug={slug} />
          </div>
          <div ref={bodyRef} className="article-prose lg:order-1">
            <Suspense fallback={<BodySkeleton />}>
              <Body />
            </Suspense>
          </div>
        </div>

        <footer className="mt-16 border-t border-slate-800 pt-8">
          <p className="mb-8 text-gray-400">
            Written by {AUTHOR_NAME}, from experience on client projects.
          </p>

          {related.length > 0 && (
            <section aria-labelledby="related-articles" className="mb-10">
              <h2
                id="related-articles"
                className="mb-4 text-2xl font-bold text-white"
              >
                Related articles
              </h2>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {related.map((a) => (
                  <ArticleCard key={a.slug} article={a} />
                ))}
              </div>
            </section>
          )}

          <nav
            aria-label="More articles"
            className="mb-8 flex flex-wrap justify-between gap-4"
          >
            {previous ? (
              <Link
                to="/articles/$slug"
                params={{ slug: previous.slug }}
                className="text-cyan-400 hover:text-cyan-300"
              >
                <span aria-hidden="true">← </span>
                <span className="sr-only">Previous article: </span>
                {previous.title}
              </Link>
            ) : (
              <span />
            )}
            {next && (
              <Link
                to="/articles/$slug"
                params={{ slug: next.slug }}
                className="text-cyan-400 hover:text-cyan-300"
              >
                <span className="sr-only">Next article: </span>
                {next.title}
                <span aria-hidden="true"> →</span>
              </Link>
            )}
          </nav>

          <Link
            to="/articles"
            className="text-cyan-400 underline underline-offset-4 hover:text-cyan-300"
          >
            Back to all articles
          </Link>
        </footer>
      </article>
      <ConsultCTA />
    </>
  );
};

export default ArticlePage;
